import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { ROUTES, OTP_EXPIRY_TIME, customToast, DINING_METHOD } from '@common'
import { useMembershipRedux, useUserRedux } from '@hooks'
import { ErrorTrackingAPI } from '@services'
import { otpSchema } from '@utils/validationSchemas'
import { verifyOTP } from '@utils/otpEncryption'

const OTPVerificationPage = () => {
    const navigate = useNavigate()
    const {
        otpData,
        setOTPVerified,
        setError: setReduxError,
        clearError,
        isLoading,
        error,
        getMemberAccessToken,
        getCurrentMember,
        requireOTP,
        registerMember,
    } = useMembershipRedux()

    const { diningMethod, tableId, pickupDate, pickupTime } = useUserRedux()
    const { phone, name, isLogin, isRememberMe, encryptedOTP, otpExpiry } = otpData

    const [otp, setOtp] = useState(['', '', '', '', '', ''])
    const [isVerifying, setIsVerifying] = useState(false)
    const [timeLeft, setTimeLeft] = useState(OTP_EXPIRY_TIME) // OTP_EXPIRY_TIME seconds
    const [canResend, setCanResend] = useState(false)

    // React Hook Form setup with Yup validation
    const {
        handleSubmit,
        formState: { errors },
        setValue,
        // watch,
        clearErrors,
    } = useForm({
        resolver: yupResolver(otpSchema),
        defaultValues: {
            otp: '',
        },
    })

    useEffect(() => {
        if (!phone) {
            navigate(ROUTES.Membership.auth)
            return
        }

        // Check if OTP is expired using Redux otpExpiry
        if (otpExpiry && Date.now() > otpExpiry) {
            setReduxError('OTP has expired. Please request a new one.')
            setCanResend(true)
            return
        }

        // Start countdown timer
        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    setCanResend(true)
                    return 0
                }
                return prev - 1
            })
        }, 1000)

        return () => clearInterval(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phone, navigate, otpExpiry])

    // Update form value when OTP array changes
    useEffect(() => {
        const otpString = otp.join('')
        setValue('otp', otpString)
        if (otpString.length === 6) {
            clearErrors('otp')
        }
    }, [otp, setValue, clearErrors])

    const handleOtpChange = (index, value) => {
        if (value.length > 1) return // Prevent multiple characters

        const newOtp = [...otp]
        newOtp[index] = value
        setOtp(newOtp)

        // Auto-focus next input
        if (value && index < 5) {
            const nextInput = document.getElementById(`otp-${index + 1}`)
            if (nextInput) nextInput.focus()
        }
    }

    const handleKeyDown = (index, e) => {
        // Handle backspace
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            const prevInput = document.getElementById(`otp-${index - 1}`)
            if (prevInput) prevInput.focus()
        }
    }

    const handlePaste = e => {
        e.preventDefault()
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '')
        if (pastedData.length === 6) {
            const newOtp = pastedData.split('')
            setOtp(newOtp)
        }
    }

    const onSubmit = async data => {
        setIsVerifying(true)
        clearError()

        try {
            const userEnteredOtp = otp.join('')

            // check if OTP is expired using Redux otpExpiry
            if (otpExpiry && Date.now() > otpExpiry) {
                setReduxError('OTP has expired. Please request a new one.')
                return
            }

            // verify OTP using encrypted OTP
            if (!(await verifyOTP(userEnteredOtp, encryptedOTP))) {
                setReduxError('Invalid OTP. Please check your code and try again.')
                return
            }

            // OTP verified successfully, get access token
            const result = await getMemberAccessToken({
                phone: phone,
                isRememberMe: isRememberMe || false,
            })

            // Check if the API call was successful
            if (result.type.endsWith('/fulfilled')) {
                // Set OTP as verified
                setOTPVerified(true)

                // Get member data using the access token
                try {
                    const memberResult = await getCurrentMember()
                    if (!memberResult.type.endsWith('/fulfilled')) {
                        ErrorTrackingAPI.sendError(memberResult.payload, 'OTPVerificationPage', 'getCurrentMember')
                    }
                } catch (error) {
                    ErrorTrackingAPI.sendError(error, 'OTPVerificationPage', 'getCurrentMember')
                }

                // Determine navigation based on user's previous selections
                let shouldGoToMenu = false

                if (diningMethod.value === DINING_METHOD.DineIn) {
                    // DineIn: Check if tableId is selected
                    shouldGoToMenu = tableId?.value !== null && tableId?.value !== undefined
                } else if (diningMethod.value === DINING_METHOD.Takeaway) {
                    // Takeaway: Always go to menu
                    shouldGoToMenu = true
                } else if (diningMethod.value === DINING_METHOD.AdvanceTakeaway) {
                    // Advance Takeaway: Check if pickupDate and pickupTime are both selected
                    const hasPickupDate = pickupDate?.value !== null && pickupDate?.value !== undefined
                    const hasPickupTime = pickupTime?.value !== null && pickupTime?.value !== undefined
                    shouldGoToMenu = hasPickupDate && hasPickupTime
                }

                if (shouldGoToMenu) {
                    // User has already made all required selections, go back to menu
                    navigate(ROUTES.Table.root)
                } else {
                    // User hasn't made required selections yet, go to root
                    navigate(ROUTES.root)
                }
            } else {
                // Handle API error
                const errorMessage = result.payload || 'Failed to get access token. Please try again.'
                setReduxError(errorMessage)
            }
        } catch (err) {
            ErrorTrackingAPI.sendError(err, 'OTPVerificationPage', 'handleSubmit')
            setReduxError('An unexpected error occurred. Please try again.')
        } finally {
            setIsVerifying(false)
        }
    }

    const handleResend = async () => {
        setCanResend(false)
        setTimeLeft(OTP_EXPIRY_TIME) // OTP_EXPIRY_TIME seconds
        clearErrors('otp')
        clearError()

        try {
            let result
            if (isLogin) {
                // Resend OTP for existing member
                result = await requireOTP({
                    phone: phone,
                })
            } else {
                // Resend OTP for registration
                result = await registerMember({
                    name: name,
                    phone: phone,
                })
            }

            if (result.type.endsWith('/fulfilled')) {
                // OTP resent successfully, encrypted OTP will be automatically handled in Redux extraReducers
                customToast.success('OTP resent successfully', { duration: 3000 })
                setOtp(['', '', '', '', '', ''])
            } else {
                const errorMessage = result.payload || 'Failed to resend OTP. Please try again.'
                setReduxError(errorMessage)
                setCanResend(true)
            }
        } catch (err) {
            ErrorTrackingAPI.sendError(err, 'OTPVerificationPage', 'handleResendOTP')
            setReduxError('An unexpected error occurred. Please try again.')
            setCanResend(true)
        }
    }

    const formatTime = seconds => {
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        return `${mins}:${secs.toString().padStart(2, '0')}`
    }

    return (
        <div className="otp-verification-page">
            <div className="otp-container">
                <div className="otp-header">
                    <h2>Verify Your Phone</h2>
                    <p>We've sent a 6-digit code to</p>
                    <p className="verification-target">{phone}</p>
                </div>

                {error && (
                    <div className="alert alert-danger">
                        <i className="bx bx-error-circle"></i>
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="otp-inputs">
                        {otp.map((digit, index) => (
                            <input
                                key={index}
                                id={`otp-${index}`}
                                type="number"
                                maxLength="1"
                                value={digit}
                                onChange={e => handleOtpChange(index, e.target.value)}
                                onKeyDown={e => handleKeyDown(index, e)}
                                onPaste={handlePaste}
                                className={errors.otp ? 'error' : ''}
                            />
                        ))}
                    </div>

                    {errors.otp && <div className="alert alert-danger text-center mb-3">{errors.otp.message}</div>}

                    <button type="submit" disabled={isVerifying || isLoading || otp.join('').length !== 6} className="btn-verify">
                        {isVerifying || isLoading ? 'Verifying...' : 'Verify Code'}
                    </button>
                </form>

                <div className="resend-section">
                    {!canResend ?
                        <p className="countdown">Resend code in {formatTime(timeLeft)}</p>
                    :   <button onClick={handleResend} className="resend-btn">
                            Resend code
                        </button>
                    }
                </div>

                <div className="back-link">
                    {/* <button onClick={() => navigate(ROUTES.Membership.auth)}>← Back to {isLogin ? 'Login' : 'Sign Up'}</button> */}
                    <button onClick={() => navigate(ROUTES.Membership.auth)}>← Change Phone Number</button>
                </div>
            </div>
        </div>
    )
}

export default OTPVerificationPage
